<template>
  <!-- eslint-disable  max-len -->
  <div
    id="bcid-authenticator"
    class="configuration"
  >
    <template v-if="showSurveyModal">
      <survey-modal
        :survey="bcidSettings.wellnessSurvey"
        :default-questions="defaultSurveyQuestions"
        @close="toggleSurveyModal()"
        @save="saveSurvey"
      >
      </survey-modal>
    </template>
    <template v-if="showHIDSetupModal">
      <setup-hid-modal @close="toggleHIDSetupModal" />
    </template>
    <!-- ADD/ACTIVATE VIEW -->
    <template v-if="isActivateMode">
      <div class="mb-2">
        <strong>BioConnect Mobile</strong> is our free 2FA authentication solution. You’ll be able
        to manage your digital and physical access all in one application.
      </div>
      <div class="flex-grid">
        <div class="column--two">
          <div class="mb-2 form-label form-label--descriptive">
            <info-tag
              :tooltip="premiumTag.tooltip"
              :tag="premiumTag.tag"
              :colour="premiumTag.colour"
            />
            Add the ability for users to declare wellness before entering your facility.
          </div>
          <label
            class="checkbox mb-1 ml-1"
            for="wellness-radio"
          >
            <input
              id="wellness-radio"
              v-model="wellnessEnabled"
              type="checkbox"
            />
            <span>Enforce Wellness Declaration</span>
          </label>
        </div>
        <div class="column--two">
          <div class="mb-1 form-label form-label--descriptive">
            This solution offers a simple allow/deny verification, or a more secure biometric
            verification.
          </div>
          <template v-if="wellnessEnabled">
            <label
              class="radio mb-1 ml-1"
              for="none-mode-radio"
            >
              <input
                id="none-mode-radio"
                v-model="verificationMode"
                type="radio"
                value="none"
              />
              <span>None (Card-Only)</span>
            </label>
          </template>
          <label
            class="radio mb-1 ml-1"
            for="simple-mode-radio"
          >
            <input
              id="simple-mode-radio"
              v-model="verificationMode"
              type="radio"
              value="basic"
            />
            <span>Simple Allow/Deny Verification</span>
          </label>
          <label
            class="radio ml-1"
            for="biometric-mode-radio"
          >
            <input
              id="biometric-mode-radio"
              v-model="verificationMode"
              type="radio"
              value="biometric"
            />
            <span>Biometric Verification</span>
          </label>
        </div>
      </div>
      <div class="configuration-footer">
        <div
          id="bcid-submit-error"
          class="configuration-footer__error"
        >
          {{ errorMessage }}
        </div>
        <div class="configuration-footer-actions">
          <button
            id="cancel-button-bcid"
            class="btn btn--grey mr-2"
            @click="emitCancel"
          >
            Cancel
          </button>
          <button
            id="submit-button-bcid"
            class="btn"
            @click="activate"
          >
            Activate BioConnect Mobile
          </button>
        </div>
      </div>
    </template>
    <!-- EDIT/ACTIVATED VIEW -->
    <template v-else>
      <section class="configuration-heading">
        <h4 class="m-0">
          BioConnect Mobile Authenticator App
        </h4>
        <p>This authenticator allows you to configure multiple authentication methods.</p>
        <p>
          <strong>Note: </strong>Users must be invited to use the BC Mobile App before they can use
          the authenticator. Go to <router-link to="/users">
            User Management
          </router-link> to
          invite users.
        </p>
        <div class="mt-2">
          <div class="mb-1 form-label form-label--descriptive">
            Primary 2FA application for all users
          </div>
          <label
            class="checkbox mb-1 ml-1"
            for="default-authenticator-check"
          >
            <input
              id="default-authenticator-check"
              v-model="defaultAuthenticatorCheck"
              type="checkbox"
            />
            <span>Set as Default Authenticator</span>
          </label>
        </div>
      </section>
      <template v-if="bcidSettings">
        <div class="configuration__divider" />
        <section>
          <h4 class="mb-0">
            Primary Authentication Method
          </h4>
          <p class="configuration__description">
            Select one primary authentication method to enable for your users.
          </p>
          <div class="configuration-options-row">
            <label
              v-for="option in filteredPrimaryOptions"
              :key="option.value"
              class="configuration-option-container"
              :for="`${option.value}-mode-radio`"
            >
              <div
                class="configuration-option"
                :class="{ 'configuration-option--active': verificationMode === option.value }"
              >
                <img
                  class="configuration-option__icon"
                  :src="option.image"
                />
              </div>
              <input
                :id="`${option.value}-mode-radio`"
                v-model="verificationMode"
                type="radio"
                class="configuration-option-input"
                :value="option.value"
              />
              <span class="configuration-option-input__label">{{ option.label }}</span>
            </label>
          </div>
        </section>
        <section>
          <h4 class="mb-0">
            Additional Authentication Methods (Optional)
          </h4>
          <p class="configuration__description">
            Select any other authentication methods to use in addition to your primary
            authentication method.
          </p>
          <div class="configuration-options-row">
            <label
              class="configuration-option-container"
              for="wellness-check"
            >
              <div
                class="configuration-option"
                :class="{ 'configuration-option--active': wellnessEnabled }"
              >
                <info-tag
                  class="configuration-option__tag"
                  :colour="premiumTag.colour"
                  :tooltip="premiumTag.tooltip"
                  :tag="premiumTag.tag"
                />
                <img
                  class="configuration-option__icon"
                  :src="surveyIcon"
                />
              </div>
              <input
                id="wellness-check"
                v-model="wellnessEnabled"
                type="checkbox"
                class="configuration-option-input"
              />
              <span class="configuration-option-input__label">Survey Verification</span>
              <button
                v-show="wellnessEnabled"
                id="wellness-survey-button"
                class="btn btn__text"
                @click="toggleSurveyModal()"
              >
                Configure
              </button>
            </label>
            <label
              class="configuration-option-container"
              for="mobile-only-check"
            >
              <div
                class="configuration-option"
                :class="{ 'configuration-option--active': hidMobileAccess }"
              >
                <info-tag
                  class="configuration-option__tag"
                  :colour="betaTag.colour"
                  :tooltip="betaTag.tooltip"
                  :tag="betaTag.tag"
                />
                <img
                  class="configuration-option__icon"
                  :src="mobileOnlyIcon"
                />
                <img
                  class="configuration-option__tag configuration-option__tag--bottom"
                  width="50px"
                  height="auto"
                  :src="hidLogo"
                />
              </div>
              <input
                id="mobile-only-check"
                v-model="hidMobileAccess"
                type="checkbox"
                class="configuration-option-input"
              />
              <span class="configuration-option-input__label"> Mobile-Only Verification</span>
              <div v-if="isNotProductionEnvironment">
                <button
                  v-show="hidMobileAccess"
                  id="setup-hid-button"
                  class="btn btn__text"
                  @click="toggleHIDSetupModal()"
                >
                  Configure
                </button>
              </div>
              <div v-else>
                <a
                  href="mailto:sales@bioconnect.com"
                  target="_blank"
                >Contact us!</a>
              </div>
            </label>
          </div>
        </section>
      </template>
      <div class="configuration-footer configuration-footer--activated">
        <template v-if="loadingSettings">
          <loader :size="25" />
        </template>
        <template v-else>
          <button
            id="cancel-button-bcid"
            class="btn btn--grey mr-2"
            @click="emitCancel"
          >
            Cancel
          </button>
          <button
            id="submit-button-bcid"
            class="btn"
            @click="update"
          >
            Update
          </button>
        </template>
      </div>
    </template>
  </div>
  <!-- eslint-enable  max-len -->
</template>

<script>
import cardOnlyIcon from '@/assets/card-only-icon.png'
import surveyIcon from '@/assets/icon-survey.png'
import biometricIcon from '@/assets/icon-face-mobile.png'
import mobileOnlyIcon from '@/assets/icon-reader-phone.png'
import simpleIcon from '@/assets/icon-yes-no-mobile.png'
import hidLogo from '@/assets/logos/logo-hid.png'

import settingsQueries from '@/graphql/settings.js'

import SurveyModal from '@/components/configureSurveyModal.vue'
import SetupHidModal from '@/components/hidSetupModal.vue'
import InfoTag from '@/components/infoTag.vue'
import Loader from '@/components/loader.vue'

import AuthenticatorMixin from '@/mixins/authenticatorMixin'
import {ENV} from '@/config'

export default {
  name: 'BcidConfiguration',
  components: {
    SurveyModal,
    InfoTag,
    SetupHidModal,
    Loader,
  },
  mixins: [AuthenticatorMixin, settingsQueries],
  props: [
    'isActivateMode',
    'parentError',
    'loading',
    'selectedAuthenticator',
    'defaultAuthenticator',
  ],
  data() {
    return {
      loadingSettings: false,
      wellnessEnabled: false,
      defaultAuthenticatorCheck: false,
      hidMobileAccess: false,
      showHIDSetupModal: false,
      showSurveyModal: false,
      verificationMode: null,
      bcidSettings: null,
      errorMessage: '',
      defaultSurveyQuestions: [
        'I have not experienced the new onset of, and I am not experiencing any of the known COVID-19 symptoms.\n\n- Fever - a temperature of 37.8C/100F or above, or chills\n- Cough or difficulty breathing\n- Loss of sense of smell/taste, unexplained sneezing, nasal congestion, runny nose\n- Sore throat, hoarse voice, difficulty swallowing\n- Nausea, vomiting, diarrhea, abdominal pain\n- Unexplained headache\n- Unexplained fatigue, general malaise\n',
        'I have not had contact with an individual with a probable or confirmed case of COVID-19.',
      ],
      primaryAuthOptions: [
        {
          label: 'None (Card Only)',
          value: 'none',
          image: cardOnlyIcon,
        },
        {
          label: 'Simple Yes/No Approval',
          value: 'basic',
          image: simpleIcon,
        },
        {
          label: 'Biometric Verification',
          value: 'biometric',
          image: biometricIcon,
        },
      ],
      premiumTag: {
        tooltip: 'This a premium paid feature, available free for up to 20 users.',
        tag: 'Premium',
        colour: 'purple',
      },
      betaTag: {
        tooltip: 'This a new feature, some errors may occur.',
        tag: 'beta',
        colour: 'blue',
      },
      surveyIcon,
      mobileOnlyIcon,
      hidLogo,
    }
  },
  computed: {
    isDefault() {
      return this.isDefaultAuthenticator(this.defaultAuthenticator, this.AUTH_TYPES.BCID)
    },
    isDevOrQAEnv() {
      return ENV.environment === 'development' || ENV.environment === 'qa'
    },
    isNotProductionEnvironment() {
      return ENV.environment !== 'production'
    },
    filteredPrimaryOptions() {
      return this.primaryAuthOptions.filter((option) => {
        return this.wellnessEnabled || option.value !== 'none'
      })
    },
  },
  watch: {
    /*
     * When deselecting wellness, set verification mode back to the original
     * mode unless it's set as none or bcid has not been activated yet,
     * then default to basic
     */
    wellnessEnabled() {
      if (!this.wellnessEnabled) {
        if (this.bcidSettings) {
          this.verificationMode
            = this.bcidSettings.authenticatorMode === 'none'
              ? 'basic'
              : this.bcidSettings.authenticatorMode
        } else {
          this.verificationMode = 'basic'
        }
      }
    },
  },
  created() {
    if (!this.isActivateMode) {
      this.fetchBCIDSettings()
    }
    if (this.defaultAuthenticator) {
      this.defaultAuthenticatorCheck = this.isDefault
    }
  },
  methods: {
    toggleSurveyModal() {
      this.showSurveyModal = !this.showSurveyModal
    },
    toggleHIDSetupModal() {
      this.showHIDSetupModal = !this.showHIDSetupModal
    },
    async fetchBCIDSettings() {
      this.loadingSettings = true
      try {
        const _credentialUUID = this.selectedAuthenticator.uuid
        this.bcidSettings = await this.listBCIDSettings(_credentialUUID)
        this.verificationMode = this.bcidSettings.authenticatorMode
        this.wellnessEnabled = this.bcidSettings.wellnessEnabled
        this.hidMobileAccess = this.bcidSettings.hidMobileAccess
      } catch (error) {
        console.log('Error retrieving BCID settings', error)
      } finally {
        this.loadingSettings = false
      }
    },
    /*
     * BCID doesn't need to create a credential, the account can be set to
     * 'bcid' and backend will create the required credentials. We only need
     * to use the updateSetting endpoint.
     */
    activate() {
      this.errorMessage = ''
      if (this.isEmptyString(this.verificationMode)) {
        this.errorMessage = 'Please select a verification mode.'
      } else {
        const _accountSettingPayload = {
          authenticator: this.AUTH_TYPES.BCID,
          bcidSettings: {
            authenticatorMode: this.verificationMode,
            wellnessEnabled: this.wellnessEnabled,
            wellnessSurvey: {
              uuid: null,
              questions: this.defaultSurveyQuestions,
            },
          },
        }
        this.emitUpdate(_accountSettingPayload)
      }
    },
    /*
     * Check if user is setting the authenticator to be the default
     * or if the authenticator is already the default
     * authenticator, then set to null. If another authernticator is
     * set, but the mode is updated, keep the current type and uuid.
     * If update is clicked, and no
     * action was selected, the current authenticator will remain.
     */
    update() {
      const _bcidSettings = {
        authenticatorMode: this.verificationMode,
        wellnessEnabled: this.wellnessEnabled,
        hidMobileAccess: this.hidMobileAccess,
      }
      const _accountSettingPayload = {bcidSettings: _bcidSettings}
      if (this.defaultAuthenticatorCheck) {
        _accountSettingPayload.authenticator = this.AUTH_TYPES.BCID
        _accountSettingPayload.credentialUUID = this.selectedAuthenticator.uuid
      } else if (this.isDefault && !this.defaultAuthenticatorCheck) {
        _accountSettingPayload.authenticator = this.AUTH_TYPES.NONE
        _accountSettingPayload.credentialUUID = null
      }
      this.emitUpdate(_accountSettingPayload)
    },
    saveSurvey(survey) {
      this.emitUpdate(survey)
    },
    emitUpdate(settingsPayload) {
      this.$emit('update-setting', settingsPayload)
    },
    emitCancel() {
      this.$emit('cancel')
    },
  },
}
</script>

<style lang="scss" scoped>
.configuration {
  &-heading {
    margin-bottom: 2em;
    @media #{$large-display} {
      width: 50%;
    }
  }
  &-options-row {
    @include flex-display();
    @include flex-justify-content(flex-start);
    @include flex-align-items(flex-start);
    flex-wrap: wrap;
    margin: 2em 0;
  }
  &-option {
    &-container {
      display: flex;
      align-items: center;
      flex-direction: column;
      margin-right: 2em;
      width: 125px;
      @media #{$mobile} {
        margin-bottom: 1em;
      }
    }
    position: relative;
    border: 1px solid $light-grey;
    border-radius: 4px;
    width: 100%;
    height: 125px;
    padding: 1em;
    box-sizing: border-box;
    cursor: pointer;
    &__icon {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    &-input {
      display: none;

      &__label {
        text-align: center;
        font-size: $small-font;
        margin-top: 1em;
      }
    }
    &__tag {
      position: absolute;
      top: 0;
      left: 50%;
      transform: translate(-50%, -50%);
      margin-right: 0;

      &--bottom {
        top: auto;
        bottom: 5px;
        left: 5px;
        transform: none;
        background-color: $white;
      }
    }
    &--active {
      border-color: $green;
      background-color: lighten($green, 45);
    }
  }
  &-footer {
    width: 100%;
    margin-top: 2em;
    @include flex-display();
    @include flex-justify-content(space-between);
    @include flex-align-items(center);
    &--activated {
      margin-top: 1em;
      @include flex-justify-content(flex-end);
    }
    &__error {
      color: $red;
    }
  }
  &__divider {
    width: 100%;
    border-bottom: 1px solid $light-grey;
    margin: 2em 0;
  }
  &__description {
    margin-top: 0.5em;
    margin-bottom: 1em;
    font-size: $small-font;
  }
}
</style>
